import { Box, FormControlLabel, Grid, Stack, Switch } from '@mui/material'
import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import FlagIcon from '@/components/dataDisplay/FlagIcon'
import InfoText from '@/components/dataDisplay/InfoText'
import TextList from '@/components/dataDisplay/TextList'
import { INTERFACE_LANGUAGES } from '@/constants/interfaceLanguages'
import { SITE_STEPS_FIELD_WIDTH } from '@/features/site/constants'
import type { NotificationPreferences } from '@/features/site/types/site'

type SiteNotificationPreferencesSettingsDisplayProps = {
  notificationPreferences?: NotificationPreferences
  onEmailEnabledChange: (isEmailEnabled: boolean) => void
  onSmsEnabledChange: (isSmsEnabled: boolean) => void
}

function SiteNotificationPreferencesSettingsDisplay({
  notificationPreferences,
  onEmailEnabledChange,
  onSmsEnabledChange,
}: Readonly<SiteNotificationPreferencesSettingsDisplayProps>) {
  const { t } = useTranslation()

  const language = INTERFACE_LANGUAGES.find((language) => language.value === notificationPreferences?.language)
  const phoneNumbers = notificationPreferences?.phoneNumbers ?? []
  const email = notificationPreferences?.email ?? ''

  function handleEmailEnabledChange(evt: ChangeEvent<HTMLInputElement>) {
    const { checked } = evt.target

    onEmailEnabledChange(checked)
  }

  function handleSmsEnabledChange(evt: ChangeEvent<HTMLInputElement>) {
    const { checked } = evt.target

    onSmsEnabledChange(checked)
  }

  return (
    <Grid container direction="column" gap={5}>
      <Grid size={{ md: 8, sm: 6 }}>
        <InfoText text={t('sites.add_new.form.notifications.info')} />
      </Grid>

      {language && (
        <Grid width={SITE_STEPS_FIELD_WIDTH}>
          <Stack flexDirection="column">
            <CustomTypography color="grey.500" variant="caption">
              {t('customer_settings.form.interface_language_label')}
            </CustomTypography>

            <Stack alignItems="center" flexDirection="row" gap={1}>
              <FlagIcon countryCode={language.countryCode} />

              <CustomTypography variant="body1">{language.name}</CustomTypography>
            </Stack>
          </Stack>
        </Grid>
      )}

      <Grid width={SITE_STEPS_FIELD_WIDTH}>
        <Stack flexDirection="column" gap={2}>
          <Box>
            <CustomTypography
              sx={{
                marginBottom: 1,
              }}
              variant="h5"
            >
              {t('sites.add_new.form.notifications.message_types.label')}
            </CustomTypography>
          </Box>

          <Stack flexDirection="column" gap={3}>
            <FormControlLabel
              control={<Switch checked={notificationPreferences?.isEmailEnabled} onChange={handleEmailEnabledChange} />}
              label={email || t('common.email')}
            />

            <FormControlLabel
              control={<Switch checked={notificationPreferences?.isSmsEnabled} onChange={handleSmsEnabledChange} />}
              label={
                phoneNumbers.length > 0 ? (
                  <TextList limit={2} values={phoneNumbers} />
                ) : (
                  t('sites.add_new.form.notifications.message_types.sms')
                )
              }
            />
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default SiteNotificationPreferencesSettingsDisplay
